import { useMemo } from "react";

// Color-code each agent role for the mono-terminal feed rows.
const ROLE_STYLE = {
  market_analyst:       "text-[var(--aurion-gold)]",
  fundamentals_analyst: "text-[var(--aurion-gold)]",
  bull_researcher:      "text-[var(--aurion-lavender)]",
  bear_researcher:      "text-[var(--aurion-lavender)]",
  risk_manager:         "text-[var(--aurion-red)]",
  portfolio_manager:    "text-[var(--aurion-gold)] font-semibold",
  system:               "text-[var(--aurion-muted-strong)]",
};

const KIND_BORDER = {
  system:   "border-l-[var(--aurion-border-strong)]",
  agent:    "border-l-[var(--aurion-lavender)]",
  verdict:  "border-l-[var(--aurion-gold)]",
  proposal: "border-l-[var(--aurion-gold)]",
  order:    "border-l-[var(--aurion-green)]",
  alert:    "border-l-[var(--aurion-red)]",
};

const KIND_LABEL = {
  system:   "sys",
  agent:    "agent",
  verdict:  "verdict",
  proposal: "proposal",
  order:    "order",
  alert:    "alert",
};

function deriveRoleFromTitle(title, kind) {
  // Titles are shaped like "MARKET ANALYST · AAPL"; slice the prefix.
  const upper = String(title || "").toUpperCase();
  if (upper.startsWith("MARKET ANALYST"))  return "market_analyst";
  if (upper.startsWith("FUNDAMENTALS"))    return "fundamentals_analyst";
  if (upper.startsWith("BULL"))            return "bull_researcher";
  if (upper.startsWith("BEAR"))            return "bear_researcher";
  if (upper.startsWith("RISK"))            return "risk_manager";
  if (upper.startsWith("PM") || upper.startsWith("VERDICT")) return "portfolio_manager";
  if (upper.startsWith("SCANNER"))         return "system";
  return kind === "verdict" ? "portfolio_manager" : "system";
}

export default function DeskFeed({ events }) {
  const rendered = useMemo(() => (events || []).slice(0, 200), [events]);
  return (
    <div className="flex h-full flex-col">
      <div className="mb-2 flex items-baseline justify-between">
        <div className="aurion-section-title">Feed</div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-[var(--aurion-muted)]">
          agent bus · audit-bound
        </div>
      </div>
      <div
        data-testid="desk-feed"
        className="aurion-scroll flex-1 space-y-1 overflow-auto pr-1"
      >
        {rendered.length === 0 ? (
          <div className="border border-dashed border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] p-6 text-center text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
            — no events yet · run an analysis or execute a trade —
          </div>
        ) : (
          rendered.map((e) => {
            const role = deriveRoleFromTitle(e.title, e.kind);
            const roleColor = ROLE_STYLE[role] || ROLE_STYLE.system;
            return (
              <div
                key={e.id}
                data-testid={`feed-item-${e.kind}`}
                className={`feed-row border-l-2 bg-[var(--aurion-panel-2)]/70 px-2.5 py-1.5 ${
                  KIND_BORDER[e.kind] || KIND_BORDER.system
                }`}
              >
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="font-mono text-[9px] tabular-nums text-[var(--aurion-muted)]">
                    {formatTs(e.ts)}
                  </span>
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${roleColor}`}>
                    {KIND_LABEL[e.kind] || e.kind}
                  </span>
                  <span className={`min-w-0 truncate text-[11px] font-medium ${
                    e.kind === "verdict" || e.kind === "proposal"
                      ? "text-[var(--aurion-text)]"
                      : "text-[var(--aurion-text-dim)]"
                  }`}>
                    {e.title}
                  </span>
                </div>
                {e.body ? (
                  <div className="mt-0.5 whitespace-pre-wrap pl-[54px] text-[10.5px] leading-snug text-[var(--aurion-muted-strong)]">
                    {e.body}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function formatTs(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  } catch {
    return "";
  }
}
